/**
 * Servicio para gestión de sucursales a través de la nueva API
 * Centraliza todas las operaciones de sucursales con la API backend
 */

import { apiHelpers } from './normalizedApiClient';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { API_ENDPOINTS } from '../config/api';
import { sucursalTransforms } from '../utils/dataTransforms';

/**
 * Obtiene todas las sucursales (SOLO para uso del Sidebar)
 * Otros componentes deben usar useSucursalesFromContext hook
 * @param {number} empresaId - ID de la empresa (opcional)
 * @returns {Promise<Array>} - Lista de sucursales
 */
export const getSucursales = async (empresaId = null) => {
  try {
    //console.log('🏢 [SucursalesService] Obteniendo datos');
    //console.log('🔍 [SucursalesService] Obteniendo sucursales desde API...', { empresaId });
    //console.log('🔍 [SucursalesService] Endpoint:', API_ENDPOINTS.SUCURSALES.BASE);
    
    const params = {};
    if (empresaId) {
      params.empresa_id = empresaId;
    }
    
    const response = await apiHelpers.sucursales.get(API_ENDPOINTS.SUCURSALES.BASE, { params });
    
    //console.log('🔍 [SucursalesService] Response received:', response);
    
    if (response.data) {
      const sucursalesData = response.data.data || response.data.sucursales || response.data;
      const transformedData = sucursalTransforms.fromApiList(sucursalesData);
      //console.log('✅ [SucursalesService] Sucursales transformadas:', transformedData.length);
      return transformedData;
    } else {
      console.warn('⚠️ [SucursalesService] No data received from API');
      return [];
    }
  } catch (error) {
    console.error('❌ [SucursalesService] Error al obtener sucursales:', error);
    console.error('❌ [SucursalesService] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url
    });
    throw error;
  }
};

/**
 * Crea una nueva sucursal
 * @param {Object} sucursal - Datos de la sucursal
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<Object>} - Sucursal creada
 */
export const createSucursal = async (sucursal, empresaId = null) => {
  try {
    //console.log('🏢 [SucursalesService] Creando registro');
    //console.log('🔄 [SucursalesService] Creando nueva sucursal...');
    
    const sucursalData = sucursalTransforms.toApi(sucursal, empresaId);
    const response = await apiHelpers.sucursales.post(API_ENDPOINTS.SUCURSALES.BASE, sucursalData);
    
    if (response.data) {
      const sucursalResponse = response.data.data || response.data.sucursal || response.data;
      //console.log('✅ [SucursalesService] Sucursal creada exitosamente');
      return sucursalTransforms.fromApi(sucursalResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [SucursalesService] Error al crear sucursal:', error);
    throw error;
  }
};

/**
 * Actualiza una sucursal existente
 * @param {string|number} id - ID de la sucursal
 * @param {Object} sucursal - Datos actualizados
 * @returns {Promise<Object>} - Sucursal actualizada
 */
export const updateSucursal = async (id, sucursal) => {
  try {
    //console.log('🏢 [SucursalesService] Actualizando registro');
    //console.log(`🔄 [SucursalesService] Actualizando sucursal ${id}...`);
    
    const sucursalData = sucursalTransforms.toApi(sucursal);
    const response = await apiHelpers.sucursales.put(API_ENDPOINTS.SUCURSALES.BY_ID(id), sucursalData);
    
    if (response.data) {
      const sucursalResponse = response.data.data || response.data.sucursal || response.data;
      //console.log('✅ [SucursalesService] Sucursal actualizada exitosamente');
      return sucursalTransforms.fromApi(sucursalResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [SucursalesService] Error al actualizar sucursal:', error);
    throw error;
  }
};

/**
 * Elimina una sucursal
 * @param {string|number} id - ID de la sucursal
 * @returns {Promise<string>} - ID de la sucursal eliminada
 */
export const deleteSucursal = async (id) => {
  try {
    //console.log('🏢 [SucursalesService] Eliminando registro');
    //console.log(`🔄 [SucursalesService] Eliminando sucursal ${id}...`);
    
    await apiHelpers.sucursales.delete(API_ENDPOINTS.SUCURSALES.BY_ID(id));
    //console.log('✅ [SucursalesService] Sucursal eliminada exitosamente');
    
    return id.toString();
  } catch (error) {
    console.error('❌ [SucursalesService] Error al eliminar sucursal:', error);
    throw error;
  }
};

/**
 * Obtiene una sucursal por su ID
 * @param {string|number} id - ID de la sucursal
 * @returns {Promise<Object|null>} - Sucursal encontrada o null
 */
export const getSucursalById = async (id) => {
  try {
    //console.log('🏢 [SucursalesService] Obteniendo datos');
    //console.log(`🔍 [SucursalesService] Obteniendo sucursal ${id}...`);
    
    const response = await apiHelpers.sucursales.get(API_ENDPOINTS.SUCURSALES.BY_ID(id));
    
    if (response.data) {
      const sucursalResponse = response.data.data || response.data.sucursal || response.data;
      //console.log('✅ [SucursalesService] Sucursal obtenida exitosamente');
      return sucursalTransforms.fromApi(sucursalResponse);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log('📭 [SucursalesService] Sucursal no encontrada');
      return null;
    }
    console.error('❌ [SucursalesService] Error al obtener sucursal:', error);
    throw error;
  }
};

/**
 * Busca sucursales por nombre o ciudad
 * @param {string} termino - Término de búsqueda
 * @returns {Promise<Array>} - Lista de sucursales encontradas
 */
export const buscarSucursales = async (termino) => {
  try {
    //console.log('🏢 [SucursalesService] Procesando operación');
    //console.log(`🔍 [SucursalesService] Buscando sucursales: "${termino}"...`);
    
    const response = await apiHelpers.sucursales.get(API_ENDPOINTS.SUCURSALES.BASE, {
      params: { q: termino }
    });
    
    if (response.data) {
      const sucursalesData = response.data.data || response.data.sucursales || response.data;
      //console.log(`✅ [SucursalesService] ${sucursalesData.length} sucursales encontradas`);
      return sucursalTransforms.fromApiList(sucursalesData);
    }
    return [];
  } catch (error) {
    console.error('❌ [SucursalesService] Error al buscar sucursales:', error);
    throw error;
  }
};

/**
 * Obtiene sucursales por ciudad
 * @param {string} ciudad - Ciudad
 * @returns {Promise<Array>} - Lista de sucursales de la ciudad
 */
export const getSucursalesPorCiudad = async (ciudad) => {
  try {
    //console.log('🏢 [SucursalesService] Obteniendo datos');
    //console.log(`🔍 [SucursalesService] Obteniendo sucursales de "${ciudad}"...`);
    
    const response = await apiHelpers.sucursales.get(API_ENDPOINTS.SUCURSALES.BASE, {
      params: { ciudad }
    });
    
    if (response.data) {
      const sucursalesData = response.data.data || response.data.sucursales || response.data;
      //console.log(`✅ [SucursalesService] ${sucursalesData.length} sucursales en ${ciudad}`);
      return sucursalTransforms.fromApiList(sucursalesData);
    }
    return [];
  } catch (error) {
    console.error('❌ [SucursalesService] Error al obtener sucursales por ciudad:', error);
    throw error;
  }
};

/**
 * Obtiene las ciudades donde hay sucursales
 * @returns {Promise<Array>} - Lista de ciudades únicas
 */
export const getCiudades = async () => {
  try {
    //console.log('🏢 [SucursalesService] Obteniendo datos');
    //console.log('🔍 [SucursalesService] Obteniendo ciudades disponibles...');
    
    const response = await apiHelpers.sucursales.get(`${API_ENDPOINTS.SUCURSALES.BASE}/ciudades`);
    
    if (response.data) {
      const ciudadesData = response.data.data || response.data.ciudades || response.data;
      //console.log(`✅ [SucursalesService] ${ciudadesData.length} ciudades disponibles`);
      return ciudadesData;
    }
    return [];
  } catch (error) {
    console.error('❌ [SucursalesService] Error al obtener ciudades:', error);
    throw error;
  }
};